import { useState, useEffect } from 'react';
import { Mail, Phone, Building2, Trash2, Search, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadsManagement = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterInterest, setFilterInterest] = useState('all');

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, searchQuery, filterInterest]);

  const fetchLeads = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  // Map "source" values from property/service forms into user-friendly interest categories,
  // so leads that never had an "interest" field can still be filtered.
  const deriveInterest = (lead) => {
    if (lead.interest) return lead.interest;
    const sourceMap = {
      'investment': 'Investor',
      'rental': 'Tenant',
      'construction': 'Construction',
    };
    return sourceMap[lead.source] || 'Other';
  };

  const filterLeads = () => {
    let filtered = [...leads];

    if (searchQuery.trim()) {
      filtered = filtered.filter(lead => 
        lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (lead.company && lead.company.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (filterInterest !== 'all') {
      filtered = filtered.filter(lead => deriveInterest(lead) === filterInterest);
    }

    setFilteredLeads(filtered);
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Error deleting lead');
    }
  };

  const interestColors = {
    'Investor': 'bg-blue-100 text-blue-600',
    'Tenant': 'bg-green-100 text-green-600',
    'PR': 'bg-purple-100 text-purple-600',
    'Careers': 'bg-orange-100 text-orange-600',
    'Construction': 'bg-yellow-100 text-yellow-700',
    'Other': 'bg-gray-100 text-gray-600',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin h-12 w-12 border-4 border-[#20B2AA] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-black mb-2">Leads Management</h1>
        <p className="text-gray-600">View and manage all contact form submissions</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              data-testid="leads-search-input"
              type="text"
              placeholder="Search by name, email, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
            />
          </div>

          {/* Interest Filter */}
          <div className="relative w-full md:w-48">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            <select
              data-testid="leads-interest-filter"
              value={filterInterest}
              onChange={(e) => setFilterInterest(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA] appearance-none cursor-pointer"
            >
              <option value="all">All Interests</option>
              <option value="Investor">Investor</option>
              <option value="Tenant">Tenant</option>
              <option value="PR">PR</option>
              <option value="Careers">Careers</option>
              <option value="Construction">Construction</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-600" data-testid="leads-count">
          Showing {filteredLeads.length} of {leads.length} leads
        </div>
      </div>

      {/* Leads List */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {filteredLeads.length === 0 ? (
          <div className="text-center py-12" data-testid="leads-empty-state">
            <p className="text-gray-500">No leads found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredLeads.map((lead) => (
              <div key={lead.id} data-testid={`lead-card-${lead.id}`} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex gap-4 flex-1">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-[#20B2AA] rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {lead.name.charAt(0).toUpperCase()}
                    </div>

                    {/* Lead Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-lg text-black" data-testid="lead-name">{lead.name}</h3>
                        <span data-testid="lead-interest-badge" className={`px-3 py-1 rounded-full text-xs font-medium ${interestColors[deriveInterest(lead)] || 'bg-gray-100 text-gray-600'}`}>
                          {deriveInterest(lead)}
                        </span>
                      </div>

                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="h-4 w-4" />
                          <a href={`mailto:${lead.email}`} className="hover:text-[#20B2AA]">{lead.email}</a>
                        </div>
                        {lead.phone && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone className="h-4 w-4" />
                            <a href={`tel:${lead.phone}`} className="hover:text-[#20B2AA]">{lead.phone}</a>
                          </div>
                        )}
                        {lead.company && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Building2 className="h-4 w-4" />
                            <span>{lead.company}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700">{lead.message}</p>
                      </div>

                      <div className="mt-2 text-xs text-gray-500">
                        Submitted on {new Date(lead.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <Button
                    data-testid={`lead-delete-${lead.id}`}
                    onClick={() => handleDelete(lead.id)}
                    className="bg-red-500 hover:bg-red-600 text-white rounded-lg ml-4"
                    size="sm"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsManagement;